import React, { useMemo } from "react";
import { Trash2, Plus } from "lucide-react";
import { Field } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";

/**
 * Feb 25 2026 — Shipping section extras on OrderDetail.
 * Handles `shipment_attempts[]` — an array of re-shipment rows so ops can
 * capture Status/Date/Tracking/Courier/Forward/RTO/Reverse charges per
 * attempt. Print label + video upload now live on the top-right action bar
 * (they're not scoped to the Shipping section anymore).
 */
export default function ShippingExtras({ order, setF, editable, dropdowns }) {
  const attempts = useMemo(() => Array.isArray(order.shipment_attempts) ? order.shipment_attempts : [], [order.shipment_attempts]);

  const addAttempt = () => {
    setF("shipment_attempts", [...attempts, {
      shipping_status: "", shipping_date: "", tracking_id: "", courier_name: "",
      forward_charges: "", rto_charges: "", reverse_charges: "",
    }]);
  };

  const patchAttempt = (idx, key, value) => {
    const next = attempts.map((a, i) => i === idx ? { ...a, [key]: value } : a);
    setF("shipment_attempts", next);
  };

  const removeAttempt = (idx) => {
    if (!window.confirm(`Remove shipment attempt #${idx + 2}?`)) return;
    setF("shipment_attempts", attempts.filter((_, i) => i !== idx));
  };

  return (
    <>
      {editable && (
        <div className="col-span-full border-t border-slate-200 mt-1 pt-3" data-testid="shipping-extras-bar">
          <Button onClick={addAttempt} testId="add-shipment-attempt-btn">
            <Plus size={12} /> Add re-shipment
          </Button>
        </div>
      )}

      {attempts.map((att, idx) => {
        const n = idx + 1;
        return (
          <div key={idx} className="col-span-full border border-slate-200 rounded-sm p-3 bg-slate-50/40" data-testid={`shipment-attempt-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11.5px] font-bold uppercase tracking-wider text-slate-700">Shipment Attempt {n + 1}</div>
              {editable && (
                <button onClick={() => removeAttempt(idx)} className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-[10.5px]"
                        data-testid={`shipment-attempt-remove-${idx}`}>
                  <Trash2 size={11} /> Remove
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Field label={`Shipping Status ${n}`}>
                <Select testId={`att-status-${idx}`} options={dropdowns?.shipping_status || []}
                        value={att.shipping_status || ""}
                        onChange={(v) => patchAttempt(idx, "shipping_status", v)} disabled={!editable} />
              </Field>
              <Field label={`Shipping Date ${n}`}>
                <Input testId={`att-date-${idx}`} type="date"
                       value={att.shipping_date || ""}
                       onChange={(e) => patchAttempt(idx, "shipping_date", e.target.value)}
                       readOnly={!editable} />
              </Field>
              <Field label={`Tracking ID ${n}`}>
                <Input testId={`att-tracking-${idx}`} value={att.tracking_id || ""}
                       onChange={(e) => patchAttempt(idx, "tracking_id", e.target.value)}
                       readOnly={!editable} />
              </Field>
              <Field label={`Courier Name ${n}`}>
                <Input testId={`att-courier-${idx}`} value={att.courier_name || ""}
                       onChange={(e) => patchAttempt(idx, "courier_name", e.target.value)}
                       readOnly={!editable} />
              </Field>
              <Field label={`Forward Charges ${n} ₹`}>
                <Input testId={`att-forward-${idx}`} type="number" value={att.forward_charges ?? ""}
                       onChange={(e) => patchAttempt(idx, "forward_charges", e.target.value === "" ? "" : Number(e.target.value))}
                       readOnly={!editable} />
              </Field>
              <Field label={`RTO Charges ${n} ₹`}>
                <Input testId={`att-rto-${idx}`} type="number" value={att.rto_charges ?? ""}
                       onChange={(e) => patchAttempt(idx, "rto_charges", e.target.value === "" ? "" : Number(e.target.value))}
                       readOnly={!editable} />
              </Field>
              <Field label={`Reverse Charges ${n} ₹`}>
                <Input testId={`att-reverse-${idx}`} type="number" value={att.reverse_charges ?? ""}
                       onChange={(e) => patchAttempt(idx, "reverse_charges", e.target.value === "" ? "" : Number(e.target.value))}
                       readOnly={!editable} />
              </Field>
            </div>
          </div>
        );
      })}
    </>
  );
}
